/**
 * Language Selector Component
 * Allows users to switch between supported languages
 */
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useI18nSafe, SUPPORTED_LANGUAGES, Language } from '../../i18n';

const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const CurrentLanguage = styled.button<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: ${props => props.$compact ? '6px 12px' : '6px 12px'};
  background: white;
  border: 1px solid #4A4E6A;
  border-radius: 24px;
  cursor: pointer;
  font-size: 14px;
  color: #4A4E6A;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: #363A50;
    background: #FAFAFB;
  }

  &:focus {
    outline: 2px solid #EA582C;
    outline-offset: 2px;
  }

  .arrow {
    transition: transform 0.2s;
  }

  &[aria-expanded="true"] .arrow {
    transform: rotate(180deg);
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  background: white;
  border: 1px solid #D9D9D9;
  border-radius: 8px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease-out;
`;

const LanguageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
`;

const LanguageOption = styled.li<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  background: ${props => props.$isSelected ? '#FFEEE9' : 'transparent'};

  &:hover {
    background: #FAFAFB;
  }

  &:focus {
    outline: 2px solid #EA582C;
    outline-offset: -2px;
    background: #FFEEE9;
  }

  .lang-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .native-name {
    font-size: 14px;
    color: #363A50;
    font-weight: ${props => props.$isSelected ? 600 : 400};
  }

  .english-name {
    font-size: 12px;
    color: #6E6E6E;
  }

  .check-mark {
    color: #EA582C;
  }
`;

interface LanguageSelectorProps {
  compact?: boolean;
  showNativeName?: boolean;
  className?: string;
  ariaLabel?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  compact = false,
  showNativeName = true,
  className,
  ariaLabel = 'Select language',
}) => {
  const { language, setLanguage } = useI18nSafe();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          // Focus next item
          const items = listRef.current?.querySelectorAll('li');
          const currentIndex = Array.from(items || []).findIndex(item => item === document.activeElement);
          const nextIndex = Math.min(currentIndex + 1, (items?.length || 1) - 1);
          (items?.[nextIndex] as HTMLElement)?.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          const items = listRef.current?.querySelectorAll('li');
          const currentIndex = Array.from(items || []).findIndex(item => item === document.activeElement);
          const prevIndex = Math.max(currentIndex - 1, 0);
          (items?.[prevIndex] as HTMLElement)?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        if (!isOpen) {
          event.preventDefault();
          setIsOpen(true);
        }
        break;
    }
  };

  const selectLanguage = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  return (
    <SelectorContainer ref={containerRef} className={className} onKeyDown={handleKeyDown}>
      <CurrentLanguage
        $compact={compact}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
      >
        <Globe size={16} aria-hidden="true" />
        <span>
          {showNativeName ? currentLang.nativeName : currentLang.name}
        </span>
        <ChevronDown size={16} className="arrow" aria-hidden="true" />
      </CurrentLanguage>

      <Dropdown $isOpen={isOpen} role="listbox" aria-label="Available languages">
        <LanguageList ref={listRef}>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <LanguageOption
              key={lang.code}
              $isSelected={language === lang.code}
              onClick={() => selectLanguage(lang.code)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectLanguage(lang.code);
                }
              }}
              role="option"
              aria-selected={language === lang.code}
              tabIndex={isOpen ? 0 : -1}
            >
              <div className="lang-info">
                <span className="native-name">{lang.nativeName}</span>
                {lang.nativeName !== lang.name && (
                  <span className="english-name">{lang.name}</span>
                )}
              </div>
              {language === lang.code && (
                <span className="check-mark" aria-hidden="true"><Check size={16} /></span>
              )}
            </LanguageOption>
          ))}
        </LanguageList>
      </Dropdown>
    </SelectorContainer>
  );
};

export default LanguageSelector;
